import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule, MatRowDef } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { ICreateBusinessPartnerContact } from './interfaces/create-business-partner-contact.interface';
import { BusinessPartnerContactService } from './business-partner-contact.service';
import { IUpdateBusinessPartnerContact } from './interfaces/update-business-partner-contact.interface';
import { BusinessPartnerContactFunctionAttributes } from '@albatrosdeveloper/ave-models-package';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { Subscription } from 'rxjs';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { NoFiltersAvailableComponent } from 'app/common/components/no-filters-available/no-filters-available.component';


@Component({
  selector: 'app-business-partner-function',
  standalone: true,
  templateUrl: './business-partner-contact.component.html',
  styleUrl: './business-partner-contact.component.scss',
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRowDef,
    MatSidenavContainer,
    FormsModule,
    NgSelectModule,
    StatusFiltersComponent,
    HoverMenuDirective,
    FilterComponent,
    NoFiltersAvailableComponent
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
})

export class BusinessPartnerContactComponent implements AfterViewInit,OnDestroy {
  displayedColumns: string[] = [ "name", "statusId", "actions"];
  currentCreateBusinessPartnerContact: ICreateBusinessPartnerContact = this.initializeCreateBusinessPartnerContact();
  currentUpdateBusinessPartnerContact: BusinessPartnerContactFunctionAttributes | undefined;
  dataSource: BusinessPartnerContactFunctionAttributes[] = [];
  searchQuery = '';
  totalItems = 0;
  pageSize = 50;
  pageIndex = 0;

  dialogRefAdd: MatDialogRef<any>;
  dialogRefEdit:MatDialogRef<any>;

  @ViewChild("editModal") editModal!: TemplateRef<any>;
  @ViewChild("addModal") addModal!: TemplateRef<any>;
  @ViewChild("deleteDialog") deleteDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;
  
  //BUSCADOR
  searchSubscription: Subscription;

  selectedStatuses: [] = [];

  constructor(
    private readonly businessService: BusinessPartnerContactService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) { }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.setupPaginationAndSorting();
    this.findAllWithCount();
  }

  private initializeCreateBusinessPartnerContact(): ICreateBusinessPartnerContact {
    return { code: '', name: ""};
  }

  private setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findAllWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.findAllWithCount(); 
  }


  findAllWithCount(): void {
    const pageIndex = this.paginator.pageIndex + 1;
    const sortField = this.getSortField();
    const sortDirection: "ASC" | "DESC" = this.sort.direction === "asc" ? "ASC" : "DESC";

    this.businessService
      .findAllWithCount(
        { currentPage: pageIndex, limitPerPage: this.pageSize, search: this.searchQuery, sort: sortField, sortDirection: sortDirection },
        this.selectedStatuses
      )
      .subscribe((data) => {
        this.dataSource = data?.data?.[0]?.data ?? [];
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.paginator.length = this.totalItems;
      });
  }

  private getSortField(): keyof BusinessPartnerContactFunctionAttributes {
    const validSortFields: (keyof BusinessPartnerContactFunctionAttributes)[] = ["code", "name", "statusId"];
    return validSortFields.includes(this.sort.active as keyof BusinessPartnerContactFunctionAttributes) ? (this.sort.active as keyof BusinessPartnerContactFunctionAttributes) : "code";
  }


  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    }
  }

  public onHandleSearch(){
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.paginator.pageIndex = 0;
          this.findAllWithCount();
        }
      });
  }

  trackByFn(index: number, item: BusinessPartnerContactFunctionAttributes): string {
    return item._id.toString();
  }

  compareById(item1: any, item2: any): boolean {
    return item1 && item2 ? item1._id === item2._id : item1 === item2;
  }

  clearFilters(): void {
    this.searchQuery = "";
    this.findAllWithCount();
  }

  openCreateModal(): void {
    this.currentCreateBusinessPartnerContact = this.initializeCreateBusinessPartnerContact();
    this.dialogRefAdd= this.dialog.open(this.addModal, { panelClass: "form-modal", disableClose: true })
    // this.dialogRefAdd.afterClosed().subscribe((result) => result && this.createBusinessPartnerFunction());
    this.dialogRefAdd.afterClosed().subscribe((result) => { result });
  }

  openEditModal(materialBrand: BusinessPartnerContactFunctionAttributes): void {
    this.currentUpdateBusinessPartnerContact = { ...materialBrand };
    this.dialogRefEdit= this.dialog.open(this.editModal, { panelClass: "form-modal", disableClose: true })
    // this.dialogRefEdit.afterClosed().subscribe((result) => result && this.updateBusinessPartnerFunction(materialBrand._id.toString()));
    this.dialogRefEdit.afterClosed().subscribe((result) => { result });
  }

  createBusinessPartnerFunction(): void {
    this.businessService.create(this.currentCreateBusinessPartnerContact).subscribe({
      next: () => {
        this.snackBar.open('Registro creado con éxito', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
        this.dialogRefAdd.close();
      },
      error: (err) => {
        this.snackBar.open(err.error.message || 'Error al crear el registro', 'Cerrar', { duration: 3000 });
      }
    });
  }

  updateBusinessPartnerFunction(id: string): void {
    if (this.currentUpdateBusinessPartnerContact) {
      const updatedBusinessPartnerContact: IUpdateBusinessPartnerContact = { 
         ...this.currentUpdateBusinessPartnerContact,
        };

      this.businessService.update(id, updatedBusinessPartnerContact).subscribe({
        next: () => {
          this.snackBar.open('Registro actualizado con éxito', 'Cerrar', { duration: 3000 });
          this.findAllWithCount();
          this.dialogRefEdit.close();
        },
        error: (err) => {
          this.snackBar.open(err.error.message || 'Error al actualizar el registro', 'Cerrar', { duration: 3000 });
        }
      });
    }
  }

  onStatusChange(event: boolean, materialBrand: BusinessPartnerContactFunctionAttributes): void {
    const originalStatusId = materialBrand.statusId;
    const updatedStatusId = event ? 1 : 0;
    const updatedBusinessPartnerFunction = { ...materialBrand, statusId: updatedStatusId };

    this.businessService.update(materialBrand._id.toString(), updatedBusinessPartnerFunction).subscribe({
      next: () => {
        this.findAllWithCount();
      },
      error: () => {
        materialBrand.statusId = originalStatusId; // Volver al estado original si falla
        this.snackBar.open('Error al actualizar el estado.', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
      }
    });
  }

  openDeleteDialog(materialBrand: BusinessPartnerContactFunctionAttributes): void {
    this.dialog
      .open(this.deleteDialog, { data: materialBrand, panelClass: "delete-modal", disableClose:true })
      .afterClosed()
      .subscribe((result) => result && this.delete(materialBrand._id.toString()));
  }

  delete(id: string): void {
    this.businessService.remove(id).subscribe(() => {
      this.findAllWithCount();
      this.snackBar.open('Registro eliminado con éxito', 'Cerrar', { duration: 3000 });
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}

